import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactPlaceholder from 'react-placeholder';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from 'commons/src/components/headers/PageHeader';
import SubHeader from 'commons/src/components/headers/SubHeader';
import { mediumFormLoader } from 'commons/src/components/placeholder';
import { RequiredRoleLevel } from 'commons/src/models/commonEnums';
import { ActionButton } from 'commons/src/models/menuModels';
import { paths } from '../../../constants';
import MqttPublisherCreateForm from './MqttPublisherCreateForm';
import MqttPublisherDetailView from './MqttPublisherDetailView';
import { MqttPublisherFormValues } from './mqttPublisherTypes';
import { useMqttPublisherConfigs } from './useMqttPublisherConfigs';

const AddEditMqttPublisher = (): React.ReactElement => {
    const { clientId } = useParams<'clientId'>();
    const navigate = useNavigate();
    const { t: txt } = useTranslation();
    const {
        configs,
        loading,
        createConfig,
        createLoading,
        updateConfig,
        deleteConfig,
        updateLoading,
        deleteLoading,
        updateActiveState,
        testConnection,
        testConnectionLoading,
    } = useMqttPublisherConfigs();

    const [editMode, setEditMode] = useState(false);

    const isExisting = !!clientId;
    const config = isExisting ? configs.find(c => c.clientId === clientId) : undefined;

    useEffect(() => {
        if (isExisting && !loading && !config) {
            navigate(`/${paths.mqttPublisher}`);
        }
    }, [isExisting, loading, config, navigate]);

    const handleUpdate = useCallback(
        async (values: MqttPublisherFormValues): Promise<{ clientId: string }> => {
            if (!clientId) throw new Error('Missing clientId');
            const updated = await updateConfig(clientId, values);
            setEditMode(false);
            return { clientId: updated.clientId };
        },
        [clientId, updateConfig]
    );

    const actionButton: ActionButton[] = [
        {
            onClick: (): void => navigate(`/${paths.mqttPublisher}`),
            testAttr: 'close-mqtt-publisher',
            title: 'MqttPublisher.Close',
            id: 'closeMqttPublisher',
            color: 'secondary',
            requiredRoleLevel: RequiredRoleLevel.ANY_ROLE,
            requiredGroupTypes: [],
        },
    ];

    const showForm = !isExisting || (isExisting && editMode && config);
    const showDetail = isExisting && !editMode && config;

    const pageTitle = (): string => {
        if (!isExisting) return txt('MqttPublisher.CreatePublisher');
        if (editMode) return txt('MqttPublisher.EditPublisher');
        return txt('MqttPublisher.ViewPublisher');
    };

    return (
        <>
            <PageHeader title={pageTitle()} />
            <div className="page-wrapper__medium">
                <SubHeader actionButtons={actionButton} />
            </div>
            <ReactPlaceholder ready={!loading} customPlaceholder={mediumFormLoader}>
                {showDetail && (
                    <MqttPublisherDetailView
                        config={config}
                        onEdit={(): void => setEditMode(true)}
                        onToggleActive={updateActiveState}
                        deleteConfig={deleteConfig}
                        deleteLoading={deleteLoading}
                        testConnection={testConnection}
                        testConnectionLoading={testConnectionLoading}
                    />
                )}
                {!showDetail && showForm && (
                    <MqttPublisherCreateForm
                        onSubmit={isExisting ? handleUpdate : createConfig}
                        submitLoading={isExisting ? updateLoading : createLoading}
                        initialConfig={isExisting ? config : undefined}
                    />
                )}
            </ReactPlaceholder>
        </>
    );
};

export default AddEditMqttPublisher;
